import React, { useState } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { CheckSquare2, Calendar, FileText, Eye, User } from 'lucide-react';
import ResponsiveTooltip from '../ui/ResponsiveTooltip';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { format } from 'date-fns';
import { truncateText } from '../../lib/utils';
import Pagination from '../Pagination';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const MyCompletedTasksTable = ({
  tasks = [],
  page,
  pages,
  total,
  pageSize,
  hasNext,
  hasPrev,
  onPageChange,
  onPageSizeChange
}) => {

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return 'Invalid date';
    }
  };
  const [viewCommentText, setViewCommentText] = useState('');
    const [viewCommentOpen, setViewCommentOpen] = useState(false);
  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge variant="outline" className="text-green-600">Completed</Badge>;
      case 'approved':
        return <Badge variant="outline" className="text-green-600">Completed</Badge>; // Backward compatibility
      default:
        // Convert status to proper title case
        const formattedStatus = status
          ? status
            .replace(/_/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ')
          : status;
        return <Badge variant="secondary">{formattedStatus}</Badge>;
    }
  };

  if (tasks.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-8">
          <CheckSquare2 className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Completed Tasks</h3>
          <p className="text-muted-foreground text-center">
            You haven't completed any tasks yet.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        {/* Desktop Table View */}
        <div className="hidden md:block border rounded-lg mt-2 overflow-hidden">
          <div className="max-h-[calc(100vh-290px)] overflow-auto">
            <div className="relative w-full overflow-visible">
              <table className="w-full caption-bottom text-sm">
                <thead className="sticky top-0 z-10 bg-gray-100 shadow-sm [&_tr]:border-b">
                  <tr className='bg-gray-100 border-b'>
                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Sr. No.</th>
                    <th className="h-10 px-2 text-left align-left font-medium text-muted-foreground">Task</th>
                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Approver</th>
                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Due Date</th>
                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">
                      <div className="flex items-center justify-center gap-2">
                        Submitted At
                        <Calendar className="w-4 h-4 text-gray-400" />
                      </div>
                    </th>
                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">
                      <div className="flex items-center justify-center gap-2">
                        Completed At
                        <Calendar className="w-4 h-4 text-gray-400" />
                      </div>
                    </th>
                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">
                      Completed By
                    </th>
                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Status</th>
                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Remark</th>
                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Document</th>
                  </tr>
                </thead>
                <tbody className="[&_tr:last-child]:border-0">
                  {tasks.map((task, index) => (
                    <tr key={task.id} className="border-b transition-colors hover:bg-gray-50">
                      <td className="p-2 text-center align-middle">
                        {(page - 1) * pageSize + index + 1}
                      </td>
                      <td className="p-2 text-left align-left">
                        <div className="space-y-1">
                          {task.description && task.description.length > 20 ? (
                            <ResponsiveTooltip
                              content={<p className="text-sm whitespace-pre-wrap">{task.description}</p>}
                              className="max-w-md p-3"
                            >
                              <div className="font-medium cursor-pointer mx-auto">
                                {truncateText(task.description, 20)}
                              </div>
                            </ResponsiveTooltip>
                          ) : (
                            <div className="font-medium mx-auto">{task.description}</div>
                          )}
                          {task.meeting?.title && (
                            task.meeting.title.length > 20 ? (
                              <ResponsiveTooltip
                                content={<p className="max-w-xs whitespace-pre-wrap">{task.meeting.title}</p>}
                              >
                                <span className="text-sm text-gray-600 cursor-pointer">
                                  Meeting: {truncateText(task.meeting.title, 20)}
                                </span>
                              </ResponsiveTooltip>
                            ) : (
                              <span className="text-sm text-gray-600">
                                Meeting: {task.meeting.title}
                              </span>
                            )
                          )}
                        </div>
                      </td>
                      <td className="p-2 text-center align-middle">
                        <div className="text-sm text-gray-900">
                          {task.approver?.full_name || 'Not Assigned'}
                        </div>
                      </td>
                      <td className="p-2 text-center align-middle">
                        {(task.due_date)}
                      </td>
                      <td className="p-2 text-center align-middle">
                        <div className="text-sm">
                          {task.submitted_at ? (
                            <>
                              <div>{format(new Date(task.submitted_at), 'MMM dd, yyyy')}</div>
                              <div className="text-gray-500">{format(new Date(task.submitted_at), 'hh:mm a')}</div>
                            </>
                          ) : (
                            <span className="text-gray-400">N/A</span>
                          )}
                        </div>
                      </td>
                      <td className="p-2 text-center align-middle">
                        <div className="text-sm">
                          {task.approved_at ? (
                            <>
                              <div>{format(new Date(task.approved_at), 'MMM dd, yyyy')}</div>
                              <div className="text-gray-500">{format(new Date(task.approved_at), 'hh:mm a')}</div>
                            </>
                          ) : (
                            <span className="text-gray-400">N/A</span>
                          )}
                        </div>
                      </td>
                      <td className="p-2 text-center align-middle">
                        {task.submitter?.full_name || 'N/A'}
                      </td>
                      <td className="p-2 text-center align-middle">
                        {getStatusBadge(task.status)}
                      </td>
                      <td className="p-2 text-center align-middle">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setViewCommentText(task.assignee_comment || 'No remark available');
                            setViewCommentOpen(true);
                          }}
                          disabled={!task.assignee_comment}
                          className={task.assignee_comment ? "text-red-600 hover:text-red-700" : "text-gray-400 cursor-not-allowed"}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                        </Button>
                      </td>
                      <td className="p-2 text-center align-middle">
                        {task.document_url ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(task.document_url, '_blank')}
                            className="flex items-center justify-center gap-2 mx-auto"
                          >
                            <FileText className="h-4 w-4 mr-1" />
                          </Button>
                        ) : (
                          <span className="text-sm text-muted-foreground">No document</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <Dialog open={viewCommentOpen} onOpenChange={() => setViewCommentOpen(false)}>
                <DialogContent className="sm:max-w-[500px]">
                  <DialogHeader>
                    <DialogTitle>Remark:</DialogTitle>
                  </DialogHeader>
                  <div className="whitespace-pre-wrap text-sm text-foreground">
                    {viewCommentText}
                  </div>
                </DialogContent>
              </Dialog>

        {/* Mobile Accordion View */}
        <div className="md:hidden mt-2">
          <Accordion type="single" collapsible className="w-full space-y-2">
            {tasks.map((task, index) => (
              <AccordionItem key={task.id} value={`completed-${task.id}`} className="border rounded-lg px-4 bg-white">
                <AccordionTrigger className="hover:no-underline py-4">
                  <div className="flex items-center justify-between w-full pr-4">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-500 flex-shrink-0">
                        {(page - 1) * pageSize + index + 1}.
                      </span>
                      <div className="flex-1 min-w-0">
                        {task.description && task.description.length > 20 ? (
                          <Popover>
                            <PopoverTrigger asChild>
                              <span className="text-sm font-medium text-gray-900 text-left hover:underline cursor-pointer inline-block">
                                {truncateText(task.description, 20)}
                              </span>
                            </PopoverTrigger>
                            <PopoverContent className="w-72 max-h-[200px] p-3 overflow-y-auto" align="start">
                              <div className="text-sm font-medium text-gray-900 whitespace-pre-wrap">
                                {task.description}
                              </div>
                            </PopoverContent>
                          </Popover>
                        ) : (
                          <p className="text-sm font-medium text-gray-900">{task.description}</p>
                        )}

                      </div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-4 pt-0">
                  <div className="space-y-4  pt-2 border-t">
                    {task.meeting?.title && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">Meeting</p>
                        {task.meeting.title.length > 20 ? (
                          <Popover>
                            <PopoverTrigger asChild>
                              <span className="text-sm text-gray-900 text-left hover:underline cursor-pointer inline-block">
                                {truncateText(task.meeting.title, 20)}
                              </span>
                            </PopoverTrigger>
                            <PopoverContent className="w-72 p-3" align="start">
                              <div className="text-sm text-gray-900 whitespace-pre-wrap">
                                {task.meeting.title}
                              </div>
                            </PopoverContent>
                          </Popover>
                        ) : (
                          <p className="text-sm text-gray-900">{task.meeting.title}</p>
                        )}
                      </div>
                    )}
                    <div className='flex flex-wrap justify-between'>
                      <div >
                        <p className="text-xs font-medium text-gray-500 mb-1">Approver</p>
                        <p className="text-sm text-gray-900">{task.approver?.full_name || 'Not Assigned'}</p>
                      </div>

                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">Due Date</p>
                        <p className="text-sm text-gray-900">{task.due_date}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">Completed By</p>
                        <p className="text-sm text-gray-900">{task.approver?.full_name || 'N/A'}</p>
                      </div>
                    </div>
                    <div className='flex flex-wrap justify-between'>
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">Submitted At</p>
                        <div className="text-sm text-gray-900">
                          {task.submitted_at ? (
                            <>
                              <div>{format(new Date(task.submitted_at), 'MMM dd, yyyy')}</div>
                              <div className="text-gray-500">{format(new Date(task.submitted_at), 'hh:mm a')}</div>
                            </>
                          ) : (
                            <span className="text-gray-400">N/A</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">Completed At</p>
                        <div className="text-sm text-gray-900">
                          {task.approved_at ? (
                            <>
                              <div>{format(new Date(task.approved_at), 'MMM dd, yyyy')}</div>
                              <div className="text-gray-500">{format(new Date(task.approved_at), 'hh:mm a')}</div>
                            </>
                          ) : (
                            <span className="text-gray-400">N/A</span>
                          )}
                        </div>
                      </div>
                      {task.document_url && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Document</p>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(task.document_url, '_blank')}
                            className="flex items-center justify-center gap-2"
                          >
                            <FileText className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>



                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Pagination */}
        {total > 0 && (
          <Pagination
            page={page}
            pages={pages}
            total={total}
            pageSize={pageSize}
            hasNext={hasNext}
            hasPrev={hasPrev}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default MyCompletedTasksTable;